'use client';

import classNames from 'classnames';
import { CmsOffer } from '../../../../../../types/cmsProduct';
import { useCmsClientContext } from '../../../../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../../../../utils/translations/getClientTranslations';
import { tr } from '../../../../../../utils/translations/tr';
import { MaterialIcon } from '../../../../../MaterialIcon';
import Tooltip from '../../../../../Tooltip';
import { ProductDetailsSectionWrapper } from '../../../../BaseComponents/ProductDetailsSectionWrapper';
import { FormattedOfferReward } from '../../formatOfferReward';
import { TRANSLATIONS } from '../../i18n';

export type ProductDetailsSectionOffersPlSummaryCardProps = {
  /** "Based on your … of … over … months"; absent without a loan selection. */
  leadIn?: string;
  /** `reminder` replaces the total value when no offer matches. */
  total: FormattedOfferReward | { kind: 'reminder'; text: string };
  offers: {
    key: string;
    title: string;
    type: CmsOffer['type'];
    reward: FormattedOfferReward;
  }[];
  onSeeMore: () => void;
};

// Summary card of the PL Offers section: what the current loan selection
// earns — the total ("Max rewards") and a line per offer.
export const ProductDetailsSectionOffersPlSummaryCard: React.FC<
  ProductDetailsSectionOffersPlSummaryCardProps
> = ({ leadIn, total, offers, onSeeMore }) => {
  const context = useCmsClientContext();
  const text = getClientTranslations(TRANSLATIONS, context);

  const rewardText = (reward: FormattedOfferReward) => {
    if (reward.kind === 'value') {
      return tr('up_to_value', text, { value: reward.value });
    }
    return reward.kind === 'noFixedValue'
      ? tr('see_details', text)
      : tr('not_available', text);
  };

  return (
    <ProductDetailsSectionWrapper>
      <div className="flex flex-col gap-spacing/gap/y px-spacing/padding/x py-spacing/padding/y">
        {leadIn && (
          <div className="flex flex-col gap-spacing/gap/y-xs border-b border-borders/default pb-spacing/gap/y">
            <p className="m-0 mhg-alpha-typography-subheading-2 text-text/primary">
              {leadIn}
            </p>
            <p className="m-0 mhg-alpha-typography-caption text-text/primary">
              {tr('calculator_note', text)}
            </p>
          </div>
        )}
        <div className="flex flex-col gap-spacing/gap/y-sm">
          <div className="flex items-start justify-between gap-spacing/gap/x-sm">
            <div className="flex items-center gap-spacing/gap/x-xs">
              <span className="mhg-alpha-typography-subheading-2 text-text/primary">
                {tr('max_rewards', text)}
              </span>
              <Tooltip content={tr('total_footnote', text)} onOpenEvent="click">
                <button
                  type="button"
                  aria-label="How the rewards add up"
                  className="flex text-text/secondary-lighter"
                >
                  <MaterialIcon icon="info" px={16} />
                </button>
              </Tooltip>
            </div>
            {(total.kind === 'value' || total.kind === 'noFixedValue') && (
              <span className="shrink-0 text-right mhg-alpha-typography-subheading-2 text-highlights/green-dark">
                {rewardText(total)}
              </span>
            )}
          </div>
          {total.kind === 'reminder' && (
            <p className="m-0 mhg-alpha-typography-body-2 text-text/primary">
              {total.text}
            </p>
          )}
          <ul className="m-0 flex list-none flex-col gap-spacing/gap/y-xs p-0">
            {offers.map((offer) => (
              <li
                key={offer.key}
                className="flex items-center justify-between gap-spacing/gap/x-sm"
              >
                <span
                  className={classNames(
                    'rounded-full px-spacing/gap/x-sm mhg-alpha-typography-body-2-relaxed font-bold',
                    offer.type === 'welcome'
                      ? 'bg-highlights/orange-light text-buttons/cta'
                      : 'bg-fill/primary-light text-fill/primary',
                  )}
                >
                  {offer.title}
                </span>
                <span
                  className={classNames(
                    'text-right mhg-alpha-typography-bullet-2',
                    offer.reward.kind === 'none'
                      ? 'text-text/secondary-lighter'
                      : 'shrink-0 text-text/primary',
                  )}
                >
                  {rewardText(offer.reward)}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <button
          type="button"
          onClick={onSeeMore}
          className="flex items-center gap-spacing/gap/x-xs self-start mhg-alpha-typography-caption text-text/accent"
        >
          {tr('see_more', text)}
          <MaterialIcon icon="arrow_right_alt" px={16} />
        </button>
      </div>
    </ProductDetailsSectionWrapper>
  );
};
